<script lang="ts" setup>
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { useMedianLaunchFdv } from '#public-research/composables/useMedianLaunchFdv'
import { formatValue } from '@forgd/formatter'
import { Card } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { useQuery } from '@pinia/colada'

const TOP_RETURN_TIMEFRAME = '30d'
const ALLOCATION_SKELETON_ROWS = 6

const allocationQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/allocation-by-category', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const priceChangeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/price-change-vs-initial-circ-supply', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const { medianFdv, isLoading: isMedianLoading } = useMedianLaunchFdv()

const isLoading = computed(() =>
  allocationQuery.isLoading.value
  || priceChangeQuery.isLoading.value
  || isMedianLoading.value,
)

const allocations = computed(() => {
  const categories = allocationQuery.data.value?.categories ?? []
  return categories.map((c, i) => ({
    label: c.category,
    value: c.avgAllocationPercentage,
    color: chartColors.tokenDesigner[i % chartColors.tokenDesigner.length]!,
  }))
})

const allocationMax = computed(() => {
  const max = allocations.value.length ? Math.max(...allocations.value.map(a => a.value)) : 0
  return max > 0 ? max : 1
})

const avgInitialFloat = computed(() => {
  const tokens = priceChangeQuery.data.value?.timeframes?.latest?.tokens ?? []
  if (!tokens.length) {
    return null
  }
  return tokens.reduce((acc, t) => acc + t.initialCircSupplyPercentage, 0) / tokens.length
})

const avg30dReturn = computed(() => {
  const entry = priceChangeQuery.data.value?.summary?.find(s => s.timeframe === TOP_RETURN_TIMEFRAME)
  return entry?.avgPriceChangePercentage ?? null
})

const stats = computed(() => [
  {
    label: 'Avg. Initial Float',
    value: formatValue(avgInitialFloat.value, 'percent:1'),
  },
  {
    label: 'Median Launch FDV',
    value: formatValue(medianFdv.value, 'currency:short,2'),
  },
  {
    label: `Price Change (%) vs Init. Float (${TOP_RETURN_TIMEFRAME.toUpperCase()})`,
    value: formatValue(avg30dReturn.value, 'percent:1'),
  },
])
</script>

<template>
  <Card class="grid grid-cols-1 divide-y divide-default sm:grid-cols-[330px_1fr] sm:divide-x sm:divide-y-0">
    <div class="flex flex-col gap-4 p-5">
      <h3 class="text-xs font-semibold text-primal">
        Avg. Token Allocation per Group Category
      </h3>
      <ul class="flex h-full flex-col justify-between">
        <template v-if="isLoading">
          <li v-for="i in ALLOCATION_SKELETON_ROWS" :key="`skeleton-${i}`" class="flex items-center gap-2">
            <Skeleton class="h-3 w-31 shrink-0" />
            <Skeleton class="h-4 flex-1" />
            <Skeleton class="h-3 w-10 shrink-0" />
          </li>
        </template>
        <template v-else>
          <li v-for="a in allocations" :key="a.label" class="flex items-center gap-2">
            <span class="w-31 shrink-0 truncate font-mono text-xs font-medium text-neutral-900/60">{{ a.label }}</span>
            <div class="relative h-2 flex-1 overflow-hidden bg-secondary-subtle sm:h-4">
              <div
                class="absolute inset-y-0 left-0"
                :style="{ width: `${(a.value / allocationMax) * 100}%`, background: a.color }"
              />
            </div>
            <span class="w-10 shrink-0 text-right font-mono text-xs text-primal">{{ formatValue(a.value, 'percent:2') }}</span>
          </li>
        </template>
      </ul>
    </div>

    <div class="flex flex-col divide-y divide-default">
      <div
        v-for="(s, i) in stats"
        :key="s.label"
        class="flex items-center justify-between gap-2 p-4 sm:flex-col sm:items-start sm:gap-1"
        :class="{ 'sm:flex-1': i === stats.length - 1 }"
      >
        <span class="text-xs text-secondary">{{ s.label }}</span>
        <Skeleton v-if="isLoading" class="h-6 w-24" />
        <span v-else class="text-base font-semibold text-primal">{{ s.value }}</span>
      </div>
    </div>
  </Card>
</template>
